<template>
    <div class="h-full w-full p-2">
        <div class="text-lg mb-2">Automatisering av bilag fra leverandør</div>
        <ag-grid-vue
            class="h-full w-full"
            :theme="agTheme"
            :row-data="suppliers"
            :column-defs="columnDefs"
            :grid-options="gridOptions"
        />
    </div>
</template>

<script setup lang="ts">

import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {fetchSuppliers, updateSubAccount, useSubAccounts} from '@/subaccount/subAccount.service'

import SupplierExpenseAccountCellEditor from '@/supplier-automation/SupplierExpenseAccountCellEditor.vue'
import {ColDef, ColGroupDef, GetRowIdParams, GridOptions, ValueSetterParams} from 'ag-grid-community'
import {SubAccount} from '@/subaccount/types/SubAccount'
import SupplierProjectCellEditor from '@/supplier-automation/SupplierProjectCellEditor.vue'
import SupplierDepartmentCellEditor from '@/supplier-automation/SupplierDepartmentCellEditor.vue'

const {suppliers} = useSubAccounts()

const columnDefs: Array<ColDef | ColGroupDef> = [
    {
        headerName: 'Navn',
        field: 'name'
    },
    {
        headerName: 'Org.Nr.',
        field: 'orgNo',
        width: 110
    },
    {
        headerName: 'Standardverdier',
        children: [
            {
                headerName: 'Kostnadskonto',
                field: 'metadata.suggestions.expenseAccount',
                editable: true,
                cellEditor: SupplierExpenseAccountCellEditor,
                cellEditorPopup: true,
                cellEditorPopupPosition: 'over'
            }, {
                headerName: 'Prosjekt',
                field: 'metadata.suggestions.project',
                editable: true,
                cellEditor: SupplierProjectCellEditor,
                cellEditorPopup: true,
                cellEditorPopupPosition: 'over'
            },{
                headerName: 'Avdeling',
                field: 'metadata.suggestions.department',
                editable: true,
                cellEditor: SupplierDepartmentCellEditor,
                cellEditorPopup: true,
                cellEditorPopupPosition: 'over'
            },
        ]
    },
    {
        headerName: 'Maks beløp aut. bilagsføring',
        field: 'metadata.suggestions.maxAmount',
        editable: true,
        type: 'numericColumn',
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
            min: 0,
            max: 1000000,
            step: 1
        },
        width: 240,
        valueSetter: (setterParams: ValueSetterParams) => {
            const updatedSubAcc = {
                ...setterParams.data,
                metadata: {
                    ...setterParams.data.metadata,
                    suggestions: {
                        ...setterParams.data.metadata.suggestions,
                        maxAmount: setterParams.newValue
                    }
                }
            }
            updateSubAccount(updatedSubAcc)
                .then(fetchSuppliers)

            return true
        }
    },
    {
        headerName: 'Auto-betal',
        field: 'metadata.suggestions.autoPayment',
        width: 140,
        editable: true,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        valueGetter: (node: any) => {
            return node.data.metadata?.suggestions?.autoPayment || false
        },
        valueSetter: (params: ValueSetterParams) => {
            const updatedSubAcc = {
                ...params.data,
                metadata: {
                    ...params.data.metadata,
                    suggestions: {
                        ...params.data.metadata.suggestions,
                        autoPayment: params.newValue
                    }
                }
            }
            updateSubAccount(updatedSubAcc)
                .then(fetchSuppliers)

            return true
        }
    }
]

const gridOptions: GridOptions = {
    getRowId: (params: GetRowIdParams<SubAccount>) => params.data.uuid
}

</script>

<style scoped>

</style>